
/**
 * Utilities for eSimGo <-> Shopify Operations
 */

const IS_PROD = ("true" === 'true');
const ESIMGO_API_URL = IS_PROD ? "https://api.esim-go.com" : "https://staging-api.esim-go.com";
const API_VERSION = "/v2.3";
const ESIMS_PATHS = Object.freeze({
  Esims: "/esims",
  Customers: "/customer",
  Assignments: "/assignments",
  QR: "/qr",
  Details: "/details",
});
const TOKEN_PARAM = "?token=";

const ZENDESK_URL = "https://help-austrian.breezesim.com/";
const ZENDESK_GUIDES_FALLBACK = "/categories/25505023167377-Device-Management";
const ZENDESK_GUIDES_CATEGORY = "25505023167377";
const ZENDESK_DEVICES_SECTION = "25505024401553-Compatible-Devices";

/**
 * Encrypts the given plain text with the provided key using AES-256-GCM algorithm.
 * @param {string} key - The encryption key to use.
 * @param {string} plaintext - The plain text to be encrypted.
 * @returns {string} A promise that resolves with the encrypted text.
 */
function encrypt(key, plaintext) {
  return CryptoJS.AES.encrypt(plaintext, key).toString();
}

/**
 * Decrypts the given encrypted text with the provided key using AES-256-GCM algorithm.
 * @param {string} key - The encryption key to use.
 * @param {string} encrypted - The encrypted text to be decrypted.
 * @returns {string} A promise that resolves with the decrypted plain text.
 */
function decrypt(key, encrypted) {
  return CryptoJS.AES.decrypt(encrypted, key).toString(CryptoJS.enc.Utf8);
}

/**
 * Wrapper class to convert Shopify order metadata into a time comparable order
 */
class ESimOrder {
  constructor(createdDate, esim, lineItems) {
    this.createdDate = createdDate;
    this.iccid = esim.iccid;
    this.bundle = esim.bundle;
    this.token = esim.token;
    this.ref = esim.ref;
    if (lineItems[0] != null) {
      this.productName = lineItems[0].title;
    } else {
      this.productName = "";
    }
  }
}

/**
* Wrapper class to give convenience methods for Mobile Data
*/
class MobileData {
  constructor(data) {
    this.data = data;
  }

  bytes() {
    return this.data;
  }

  kilobytes() {
    if (this.data != "Translation missing: en.sections.main_esim_details.unlimited") {
      return this.data / 1000;
    }
    return this.data;
  }

  megabytes() {
    if (this.data != "Translation missing: en.sections.main_esim_details.unlimited") {
      return this.data / 1000000;
    }
    return this.data;
  }

  gigabytes() {
    if (this.data != "Translation missing: en.sections.main_esim_details.unlimited") {
      return this.data / 1000000000;
    }
    return this.data;
  }
}

/**
 * enum for Possible states of a Bundle
 */
const BundleState = Object.freeze({
  InstalledWithBundle: Symbol("InstalledWithBundle"),
  InstalledWithNoBundle: Symbol("InstalledWithNoBundle"),
  InstalledWithBundleQueued: Symbol("InstalledWithBundleQueued"),
  NotInstalledWithBundle: Symbol("NotInstalledWithBundle"),
  NotInstalledWithNoBundle: Symbol("NotInstalledWithNoBundle"),
});

const INSTALLED = "Installed";

/**
 *  Check customer orders, check for duplicate ICCIDs (in event of topups),
 *  Return a list of the most recent ICCID+Token combo
 *  The checking on order dates may be redundant due to ordering of shopify orders
 *  but there is no harm in checking.
 * @param {[ESimOrder]} orders           A list of ESimOrder.
 * @return {[ESimOrder]}                 A list of unique ESimOrders, with the most recent token.
 */
function getUniqueEsimsFromCustomerOrders(orders) {
  // Map with ICCID as key and and the most recent order for it
  let mostRecentUniqueOrders = new Map();

  // compare against current value held against iccid key for mostRecentUniqueOrders
  // if the order date is newer, replace the entry in map
  orders.forEach((esim) => {
    // check if iccid already exists in order map
    if (mostRecentUniqueOrders.has(esim.iccid)) {
      var prevOrder = mostRecentUniqueOrders.get(esim.iccid);
      var prevDate = prevOrder.createdDate;

      // check if order being proccessed is newer than the currently held value
      if (esim.createdDate > prevDate) {
        mostRecentUniqueOrders.set(esim.iccid, esim);
        return;
      }
    } else {
      // new unique iccid
      mostRecentUniqueOrders.set(esim.iccid, esim);
    }
  });

  // return as an array of ESimOrder
  return [...mostRecentUniqueOrders].map(([_, value]) => value);
}

/**
 *
 * GET Bundles assigned to an eSim using the `/esims/customer/$ref` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customer/$ref`
 *
 * @param {ESimOrder} esim         An eSim
 * @param {string} dataKey         The data key used to decrypt the customerRef
 * @return {JSON}                  Response from eSimGo API
 */
async function getEsimAssignmentData(esim, dataKey) {
  let url =
    ESIMGO_API_URL +
    API_VERSION +
    ESIMS_PATHS.Esims +
    ESIMS_PATHS.Customers +
    "/" +
    esim.ref +
    TOKEN_PARAM +
    esim.token;

  return fetch(url, {
    method: "GET",
  }).then((response) =>
    response.json().then(async function (data) {
      if (data.message) {
        return data;
      }

      if (data.customerRef && dataKey && dataKey.length === 36) {
        data.customerRef = decrypt(dataKey, data.customerRef);
      } else {
        data.customerRef = "";
      }

      return data;
    })
  );
}

/**
* Esim Assignment Data returned from the eSimGo API
*/
class ESimInformation {
  constructor(esim_assignment_data, esim_data) {
    this.iccid = esim_assignment_data.iccid;
    this.bundle = getBundlesByStatus(esim_assignment_data.bundles);
    this.profileStatus = esim_assignment_data.profileStatus;
    this.shopifyOrderId = esim_assignment_data.shopifyOrderId;
    this.notifications = esim_assignment_data.notifications;
    this.settings = esim_assignment_data.settings;
    this.customerRef = esim_assignment_data.customerRef;

    this.productName = esim_data.productName;
    this.createdDate = esim_data.createdDate;
    this.orderRef = esim_data.ref
    this.orderToken = esim_data.token;
  }

  expirationDate() {
    if (this.bundle.active.length == 0) {
      return new Date(0);
    } else {
      return this.bundle.active[0].assignment.endTime;
    }
  }

  remainingActiveData() {
    let activeBundles = this.bundle.active.length;
    if (activeBundles == 0) {
      return new MobileData(0);
    } else {
      if (activeBundles > 1 ) {
        for (const activeBundle of this.bundle.active) {
          if (activeBundle.assignment.unlimited == false) {
            return new MobileData(activeBundle.assignment.remainingQuantity);
          }
        }
      } else {
        if (this.bundle.active[0].assignment.unlimited) {
          return new MobileData("Translation missing: en.sections.main_esim_details.unlimited");
        } else {
          return new MobileData(this.bundle.active[0].assignment.remainingQuantity);
        }
      }
    }
  }

  remainingQueuedData() {
    if (this.bundle.queued.length == 0) {
      return new MobileData(0);
    } else {
      if (this.bundle.queued[0].assignment.unlimited) {
        return new MobileData("Translation missing: en.sections.main_esim_details.unlimited");
      } else {
        return new MobileData(this.bundle.queued[0].assignment.remainingQuantity);
      }
    }
  }

  qrUrl() {
    return `${ESIMGO_API_URL}${API_VERSION}/esims/qr/${this.orderRef}?token=${this.orderToken}`;
  }
}

function getBundlesByStatus(esimBundles) {
  let activeBundles = [];
  let queuedBundles = [];
  let expiredBundles = [];

  for (let b = 0; b < esimBundles.length; b++) {
    let currentBundle = JSON.parse(JSON.stringify(esimBundles[b]));
    delete currentBundle.assignments;

    for (let ba = 0; ba < esimBundles[b].assignments.length; ba++) {
      let bundleAssignment = JSON.parse(JSON.stringify(currentBundle));
      let assignment = esimBundles[b].assignments[ba];
      bundleAssignment.assignment = assignment;
      let startTime = assignment.startTime
        ? new Date(assignment.startTime)
        : null;
      let endTime = assignment.endTime ? new Date(assignment.endTime) : null;

      // if bundle is not started, it's queued.
      if (startTime == null) {
        queuedBundles.push(bundleAssignment);
        continue;
      }

      // if endTime is past now, bundle is expired.
      if (endTime < Date.now()) {
        expiredBundles.push(bundleAssignment);
        continue;
      }

      // check to ensure Bundle is not completed, if it is then we ignore it.
      // unless its unlimited, as remaining quantity will show remaining unthrottled data
      if (assignment.remainingQuantity > 0 || assignment.unlimited) {
        activeBundles.push(bundleAssignment);
        continue;
      }

      expiredBundles.push(bundleAssignment);
    }
  }

  return {
    active: activeBundles,
    queued: queuedBundles,
    expired: expiredBundles,
  };
}

/**
 *
 * UPDATE the `customerRef` of an eSim using the `/esims/customers` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customers/$ref`
 *
 * The `customerRef` is used as a method of allowing the end user to name their eSim.
 * @param {ESimOrder} esim         An eSim
 * @param {String} customerRef     The new name of the eSim
 * @param {String} dataKey         The data key used to encrypt the customerRef
 * @return {JSON}                  Response from eSimGo API
 */
async function updateEsimCustomerReference(esim, customerRef, dataKey) {
  if (!dataKey || dataKey.length !== 36) {
    return;
  }

  let url =
    ESIMGO_API_URL +
    API_VERSION +
    ESIMS_PATHS.Esims +
    ESIMS_PATHS.Customers +
    "/" +
    esim.ref +
    TOKEN_PARAM +
    esim.token;
  return fetch(url, {
    method: "PUT",
    body: JSON.stringify({
      newName: encrypt(dataKey, customerRef),
    }),
  }).catch((err) => console.error(err));
}

/**
 *
 * UPDATE the Notification preferences of an eSim using the `/esims/customers` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customers/$ref`
 *
 * @param {ESimOrder} esim                          An eSim
 * @param {Boolean} emailNotificationsEnabled       Allow email notifications
 * @param {Boolean} smsNotificationsEnabled         Allow SMS notifications
 * @param {String} ref                              The eSIM reference
 * @param {String} token                            The eSIM token
 * @return {JSON}                                   Response from eSimGo API
 */
async function updateNotificationsPreferences(
  emailNotificationsEnabled,
  smsNotificationsEnabled,
  ref,
  token
) {
  let url =
    ESIMGO_API_URL +
    API_VERSION +
    ESIMS_PATHS.Esims +
    ESIMS_PATHS.Customers +
    "/" +
    ref +
    TOKEN_PARAM +
    token;

  return fetch(url, {
    method: "PUT",
    body: JSON.stringify({
      emailNotifications: emailNotificationsEnabled.toString(),
      smsNotifications: smsNotificationsEnabled.toString(),
    }),
  }).catch((err) => console.error(err));
}

/* Zendesk Functions */
async function getZendeskLocale(currentLocale) {
  let locale = "en-gb";

  if (currentLocale) {
    currentLocale = currentLocale.toLowerCase();

    let url = ZENDESK_URL + "/api/v2/help_center/locales";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          if (res.locales && res.locales.includes(currentLocale)) {
            return currentLocale;
          } else if (res.locales.length > 0) {
            for (let i = 0; i < res.locales.length; i++) {
              if (
                res.locales[i].substring(0, 2) == currentLocale.substring(0, 2)
              ) {
                return res.locales[i];
              }
            }
          }
          return locale;
        })
        .catch((err) => {
          return locale;
        })
    );
  }

  return locale;
}

async function getZendeskSections(locale, categoryId) {
  if (locale && categoryId) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/" +
      locale +
      "/categories/" +
      categoryId +
      "/sections";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

async function getZendeskArticlesBySection(locale, sectionId) {
  if (locale && sectionId) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/" +
      locale +
      "/sections/" +
      sectionId +
      "/articles";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

async function getZendeskArticlesByLabel(locale, sectionId, label) {
  if (locale && sectionId && label) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/articles/search.json?section=" +
      sectionId +
      "&locale=" +
      locale +
      "&label_names=" +
      label;

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}
